<template>
    <section id="interest" class="interest-card card mb-3">
        <form
              v-if="interest"
              ref="interestForm"
              class="card-body"
              v-click-outside="onClickOutside"
              @click="focusBlock"
              >
            <div class="card-heading">
                <h5 class="card-name">
                    {{ $t('lib_preference') }}
                </h5>
            </div>
            <hr />
            <p 
               class="block-error-feedback text-danger"
               v-if="blockErrorMessage(block.name, 'interests')"
               >
                <font-awesome-icon icon="fas fa-exclamation-circle" />
                {{ $t(blockErrorMessage(block.name, 'interests')) }}
            </p>
            <div
                 v-for="(item, index) in interest.interests"
                 :key="index"
                 class="row form-row"
                 >
                <div
                     class="form-group col-md-6 required-group"
                     :class="{
                        'required-group': isFieldRequired('unitId'),
                    }"
                     >
                    <label for="unitId" class="form-label">
                        {{ $t('lib_preference') }}
                    </label>
                    <Dropdown
                              appendTo="self"
                              inputId="unitId"
                              v-model="item.unitId"
                              :class="{
                            'is-invalid': errorMessage(
                                'unitId',
                                block.name,
                                'interests',
                                index
                            ),
                        }"
                              class="w-100"
                              :filter="true"
                              :showClear="true"
                              :options="unitsFilteredByApplicationCycleId"
                              optionLabel="name"
                              optionValue="id"
                              optionDisabled="optionDisabled"
                              :placeholder="$t('cand_choose')"
                              @change="toggleCommentPrefix(item, $event)"
                              >
                        <template #option="{ option }">
                            <!-- Using level to indent the dropdown as wanted by business -->
                            <div :class="[`level-${option.level}`]">
                                {{ option.name }}
                            </div>
                        </template>
                    </Dropdown>
                    <div 
                         v-if="
                            errorMessage(
                                'unitId',
                                block.name,
                                'interests',
                                index
                            )
                        "
                         class="invalid-feedback"
                         >
                        {{
                            $t(
                                errorMessage(
                                    'unitId',
                                    block.name,
                                    'interests',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div v-if="applicationTypeIsTrainee" class="form-group col-md-6">
                    <label class="form-label">
                        {{ $t('lib_click_icons') }}
                    </label>
                    <div class="d-flex">
                        <div  @click="showTrainsheepInfo($event, unitPos)" v-for="(unitPos, index2) in item.unitPositions"
                             :key="index2"
                             class="flex">
                            <font-awesome-icon icon="fas fa-info-circle fs-3 me-2 pointer" />
                        </div>
                    </div>
                </div>
                <div class="form-group col-md-6 vp-selection align-items-end pb-2" v-if="toggleVpInput(item.unitId)">
                    <label>
                        {{ $t('lib_cab_pres') }}
                    </label>
                    <InputSwitch 
                                 :id="index + '-vp-checkbox'"
                                 :binary="true"
                                 v-model="item.unitIsVp"
                                 @change="toggleCommentPrefix(item)"
                                 >
                    </InputSwitch>
                    <label>
                        {{ $t('lib_cab_vp') }}
                    </label>
                </div>
                <div
                     class="form-group col-sm-12 mb-3 required-group"
                     :class="{ 'required-group': isFieldRequired('comments') }"
                     >
                    <label for="comments" class="form-label">
                        {{ $t('lib_motivate') }}
                    </label>
                    <textarea 
                              id="comments"
                              type="text"
                              maxlength="600"
                              v-model="item.comments"
                              class="form-control"
                              :class="{
                            'is-invalid': errorMessage(
                                'comments',
                                block.name,
                                'interests',
                                index
                            ),
                        }" 
                              />
                    <p class="textarea-character-length">
                        {{ item.comments ? item.comments.length : '0' }}/600
                    </p>
                    <div 
                         v-if="
                            errorMessage(
                                'comments',
                                block.name,
                                'interests',
                                index
                            )
                        "
                         class="invalid-feedback"
                         >
                        {{
                            $t(
                                errorMessage(
                                    'comments',
                                    block.name,
                                    'interests',
                                    index
                                )
                            )
                        }}
                    </div>
                </div>
                <div class="d-flex justify-content-center">
                    <button 
                            type="button"
                            v-show="interest.interests.length > block.minItems"
                            @click="removeInterest(index)"
                            class="btn-remove"
                            v-tooltip.top.hover.focus="$t('lib_remove_interest')"
                            >
                        <font-awesome-icon icon="fas fa-trash"/>
                    </button>
                </div>

                <div class="col-sm-12 card-divider mt-2">
                    <hr />
                </div>
            </div>
            <div
                 class="add-interest-btn btn-add-wrapper"
                 @click="addInterest"
                 v-show="interest.interests.length < block.maxItems"
                 >
                <button type="button" class="btn-add">
                    <font-awesome-icon icon="fas fa-plus"/>
                </button>
                {{ $t('lib_add') }}
            </div>
            <div class="card-footer d-flex justify-content-end mt-3" v-if="formWasChanged">
                <div class="card-actions">
                    <button
                            type="button"
                            class="card-action save-action btn btn-text me-2"
                            @click="validate()"
                            :disabled="appLoading"
                            >
                        <font-awesome-icon icon="far fa-save" class="fs-5 btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_save') }}
                        </span>
                    </button>
                    <button 
                            type="button"
                            class="card-action undo-changes-action btn btn-text"
                            @click="undoChanges()"
                            :disabled="appLoading"
                            >
                        <font-awesome-icon icon="fas fa-undo" class="btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </button>
                </div>
            </div>
        </form>
    </section>
    <OverlayPanel @hide="onHidePanel" ref="op">
        <div class="flex flex-column gap-3 w-25rem">
            <div style="width:600px" v-html="trainshipDescription"></div>    
        </div>
    </OverlayPanel>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import _ from 'lodash';
import OverlayPanel from 'primevue/overlaypanel';


export default {
    components: {
        Dropdown,
        InputSwitch,
        OverlayPanel
    },
    props: {
        block: {
            type: Object,
            required: true,
        },
        applicationId: {
            type: String,
            required: true,
        },
        applicationType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            interest: null,
            interestModel: {
                id: 0,
                unitId: null,
                comments: '',
                unitIsVp: false,
                unitCode: '',
            },
            listenToClickOutside: false,
            formWasChanged: false,
            trainshipDescription: ''
        };
    },
    computed: {
        ...mapGetters([
            'blockErrorMessage',
            'errorMessage',
            'interestData',
            'applicationTypeIsTrainee',
            'applicationCycle',
            'positions',
            'isFieldRequired',
            'units',
            'appLoading',
            'isCORSite'
        ]),
        unitsFilteredByApplicationCycleId() {
            return this.units
                ?.filter((x) =>
                    this.applicationTypeIsTrainee
                        ? x.active && x.level >= 1 && this.positions?.some((y) => {
                              return (
                                  y.applicationCycleId ==
                                      this.applicationCycle.id  &&
                                  (y.unitId == x.id ||
                                      x.children?.includes(y.unitId))
                              );
                          })
                        : x.active && x.level >= 1 && x.level <= 3
                )
                ?.map((x) => {
                    return {
                        ...x,
                        optionDisabled:
                            this.applicationTypeIsTrainee &&
                                this.positions?.some(
                                    (y) =>
                                        y.applicationCycleId ==
                                            this.applicationCycle.id &&
                                        y.unitId == x.id
                                ) == false,
                    };
                });
        },
    },
    methods: {
        ...mapActions([
            'validateBlock',
            'updateBlock',
            'clearBlockErrors',
            'focusOnBlock',
        ]),
        validate() {
            this.clearBlockErrors(this.block.name);
            this.validateBlock({
                blockData: this.interest,
                blockName: this.block.name,
            }).then(
                () => {
                    // success
                    for (let interest of this.interest?.interests){
                        this.toggleCommentPrefix(interest);
                    }
                    this.updateBlock({
                        blockName: this.block.name,
                        application: {
                            applicationId: this.applicationId,
                            applicationType: this.applicationType,
                            applicationBlocks: {
                                [this.block.name]: this.interest,
                            },
                        },
                    }).then(
                        () => {
                            // success
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'success',
                                detail: this.$t('mess_save_success'),
                                life: 3000,
                            });
                            this.loadData();
                            this.$emit('checkErrorForOtherBlocks');
                        },
                        () => {
                            // error
                            this.listenToClickOutside = false;
                            this.$toast.add({
                                severity: 'error',
                                detail: this.$t('mess_save_failed'),
                                life: 3000,
                            });
                        }
                    );
                },
                () => {
                    // error
                    this.listenToClickOutside = false;
                    this.$toast.add({
                        severity: 'error',
                        detail: this.$t('mess_save_failed'),
                        life: 3000,
                    });
                }
            );
        },
        addInterest() {
            let tempData = this.interest.interests;
            if (tempData === null)
                tempData = new Array(_.cloneDeep(this.interestModel));
            else tempData.push(_.cloneDeep(this.interestModel));
            this.interest.interests = tempData;
        },
        removeInterest(index) {
            let tempData = [...this.interest.interests];
            tempData.splice(index, 1);
            this.interest.interests = tempData;
        },
        onHidePanel() {
            if (this.ev) {
                this.$refs.op.show(this.ev);
                this.ev = null;
                this.unitPos = this.crtunitPos;
                this.trainshipDescription = this.crtunitPos.description;
            } else {
                this.unitPos = null;
            }
        },
        showTrainsheepInfo(event, unitPos) { 
            const pop = this.$refs.op;
            if (this.unitPos == unitPos) {
                this.unitPos = null;
                pop.hide();            
            } else {               
                if (this.$refs.op.visible) {
                  
                    this.crtunitPos = unitPos;
                    this.ev = event;
                    pop.hide();
                 } else {
                    this.unitPos = unitPos;
                    this.trainshipDescription = unitPos.description
                    pop.show(event);
                 }                
            }
        },
        onClickOutside() {
            if (
                this.listenToClickOutside &&
                this.formWasChanged &&
                !this.appLoading
            ) {
                this.clearBlockErrors(this.block.name);
                this.validate();
            }
            this.focusOnBlock(null);
        },
        undoChanges() {
            this.loadData();
            this.clearBlockErrors(this.block.name);
        },
        loadData() {
            this.interest = _.cloneDeep(this.interestData);
            this.handleSavedVp();
        },
        focusBlock() {
            this.listenToClickOutside = true;
            this.focusOnBlock({
                name: 'interest',
                label: this.$t('lib_preference'),
            });
        },
        toggleVpInput(unitId) {//checks if the selected option in the preferred units is the presidents cabinet
            return  this.units.find(unit => unit.id === unitId &&  unit.code === 'PDT.CAB' ) &&
                    this.isCORSite &&
                    this.applicationTypeIsTrainee;
        },
        handleSavedVp() { //this is just for when data is brought in from upstream
            for (let interest of this.interest?.interests){
                if (interest.unitCode === 'PDT.CAB'){ //specific for president's cabinet
                    //the vp checkbox state is not saved in the DB!!
                    //it relies on the cabinet code and the presence of a specific string at the beginning of the comments section
                    if (interest.comments?.indexOf(this.$t('lib_cab_vp')) === 0){
                        interest.unitIsVp = true; //if user selects vp checkbox
                    } else if (interest.comments?.indexOf(this.$t('lib_cab_pres')) === 0){
                        interest.unitIsVp = false;//if user does not select vp checkbox
                    }
                }
            }
        },
        toggleCommentPrefix(interest, event) { //this handles user action
            const isVp = interest?.unitIsVp;

            let commentRef = interest?.comments;

            interest.comments = commentRef ? commentRef.replace(this.$t('lib_cab_vp') + ': ', '').replace(this.$t('lib_cab_pres') + ': ', '') : '';

            if (this.toggleVpInput(interest.unitId)){
                if (isVp){
                    interest.comments = `${this.$t('lib_cab_vp')}: ${interest.comments}`;
                } else {
                    interest.comments = `${this.$t('lib_cab_pres')}: ${interest.comments}`;
                }
            } else {
                interest.unitIsVp = false;
            }

            if (event && this.applicationTypeIsTrainee) {
                let unit = this.units.find(x => x.id == event.value && x.unitPositions.some(y => y.applicationCycleId == this.applicationCycle.id));
                interest.unitPositions = unit ? unit.unitPositions.filter(x => x.applicationId = this.applicationCycle.id && x.applicationCycleId == this.applicationCycle.id) : [];
            }
         },
    },
    mounted() {
        this.loadData();
    },
    watch: {
        // whenever interest object changes, this function will run and this way we will now if the form data was changed so we can save changes;
        interest: {
            handler(newData, oldData) {
                const storeData = {
                    ...this.interestData,
                };
                this.formWasChanged =
                    JSON.stringify(newData) !== JSON.stringify(storeData);

                if (!this.formWasChanged && oldData) {
                    // checking for oldData to prevent clearing the errors on firstload;
                    this.clearBlockErrors(this.block.name);
                }
            },
            deep: true,
        },
    },
};
</script>
